import { useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { TicketRow } from './TicketRow';
import { useTickets } from './useTickets';
import type { TicketRow as Row } from './ticketInboxModel';

/**
 * Support ticket inbox (D-ticket-inbox, the Phase 6 LEAD surface). A worklist over the
 * first-class tickets collection, organised by the ticket lifecycle: open and escalated
 * need attention on top, pending below, resolved as history. Breached-SLA tickets sort
 * to the top of their section (the breach is derived off the injected clock).
 *
 * This surface carries the phase foundation: the support-agent role and the bounded
 * support credit. Ticket actions and the support credit route through the chokepoint and
 * the audit floor (every write recorded for free).
 *
 * OPERATIONAL, SWITCHER-SCOPED: a ticket is about one member in one market, so the inbox
 * follows the region switcher scope like the chargebacks and cashout queues, NOT a global
 * view. ROLE-gated to support and super_admin: the inbox is the support agent's surface,
 * so a viewer's read-everywhere wildcard, the editor and the fraud reviewer do not see it.
 */

const Section = ({
  title,
  rows,
  emptyText,
  testId,
  now,
}: {
  title: string;
  rows: Row[];
  emptyText: string;
  testId: string;
  now: number;
}) => (
  <Box sx={{ mb: 4 }} data-testid={testId}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title} ({rows.length})
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    ) : (
      rows.map((row) => <TicketRow key={row.id} row={row} now={now} />)
    )}
  </Box>
);

/** The surface without the access wrapper. Exported so tests can inject `asOf` (the
 *  clock the SLA-breach derivation reads). */
export const TicketInboxView = ({ asOf }: { asOf?: number }) => {
  const [now] = useState<number>(() => asOf ?? Date.now());
  const { view, isLoading } = useTickets(now);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Support
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Ticket inbox
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Member support tickets as a worklist. Open and escalated tickets need
        attention, pending are in progress, resolved are history. A breached SLA
        is derived off the clock and surfaces to the top. This view follows the
        region switcher scope.
      </Typography>

      {isLoading || !view ? (
        <Loading />
      ) : (
        <>
          <Section
            title="Open"
            rows={view.open}
            emptyText="No open tickets in scope."
            testId="ticket-section-open"
            now={now}
          />
          <Divider sx={{ mb: 3 }} />
          <Section
            title="Escalated"
            rows={view.escalated}
            emptyText="No escalated tickets in scope."
            testId="ticket-section-escalated"
            now={now}
          />
          <Divider sx={{ mb: 3 }} />
          <Section
            title="Pending"
            rows={view.pending}
            emptyText="No pending tickets in scope."
            testId="ticket-section-pending"
            now={now}
          />
          <Divider sx={{ mb: 3 }} />
          <Section
            title="Resolved"
            rows={view.resolved}
            emptyText="No resolved tickets in scope."
            testId="ticket-section-resolved"
            now={now}
          />
        </>
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /tickets. ROLE-gated to support and super_admin: the inbox is
 * the support agent's surface, and a viewer's read-everywhere wildcard must not reach it
 * (D-ticket-inbox).
 */
export const TicketInbox = () => (
  <RequireAccess roles={['support', 'super_admin']}>
    <TicketInboxView />
  </RequireAccess>
);
